import gql from 'graphql-tag'
import apiConnector from '@/mixins/apiConnector'
import duplicator from '@/mixins/pagination/providers/duplicator'
import localStorageHelper from '@/assets/modules/localStorageHelper'
import cvDummy from '@/mixins/pagination/providers/dummyData/campaignViewer'
import acceptedTypes from '@/assets/configurations/mpe/acceptedTypes'
import moment from 'moment'

const eventPayload = () => `
... on MediaPlanEventFacebookPost {
  message,
  media: photos {
    url
  },
  photos {
    url
  }
}
... on MediaPlanEventSqwizArticle {
  title,
  body,
  media: images {
    url
  },
  images {
    url
  }
}
... on  MediaPlanEventSqwizProduct{
  descriptionShort,
  title,
  discount: discountPercent,
  price,
  media: images {
    url
  },
  images {
    url
  }
}
... on MediaPlanEventTwitterTweet {
  status,
  media {
    url
  }
},

... on MediaPlanEventInformacijaProduct {
  name,
  shortDescription,
  discount,
  price,
  media: images {
    url
  },
  images {
    url
  }
},
... on MediaPlanEventInformacijaPost {
  name,
  shortDescription,
  media: images {
    url
  },
  images {
    url
  }
}
... on MediaPlanEventWordpressPost {
  title,
  content,
  media {
    url
  }
},
... on MediaPlanEventWoocommerceProduct {
  name,
  shortDescription,
  description,
  regularPrice,
  salePrice,
  categories,
  media: images {
    url
  },
  images {
    url
  }
}
`

const event = () => `
  id,
  type,
  status,
  publishAt,
  removeAt,
  link,
  channel: credentials {
    id,
    type,
    label
  }
`

const advertisable = () => `
id,
label,
type,
createdAt,
meta {
  ... on AdvertisableImportedProduct {
    description,
    price,
    discount
  },
  ... on  AdvertisableProduct {
    description,
    price,
    discount
  },
  ... on  AdvertisablePost {
    description: content
  }
},
media {
  set {
    url
  }
}
`

const campaign = () => `
id,
label,
type,
startsAt,
expiresAt,
status,
meta {
  ... on CampaignSale {
    discount
  },
  ... on CampaignDailyDeal {
    discount
  }
}
`

export default {
  data () {
    return {
      mediaPlanEvents: {},
      campaigns: {},
      campaignRange: {}
    }
  },

  computed: {
    /**
     * Gives campaign range from start of first to end of last
     * @returns {Object}
     */
    getCampaignRange () {
      return this.campaignRange
    },

    /**
     * Takes an array of mpes and returns mpes, campaigns, advertisables
     */
    prepareGridObjects () {
      const campaigns = {}
      let advertisables = {}
      const items = []

      for (const mpe of this.getRawData) {
        const { campaign, advertisable } = mpe
        if (campaign) {
          if (!campaigns[campaign.id]) {
            let nc = { ...campaign, events: { set: [] }}
            items.push(nc)
            campaigns[campaign.id] = nc
          }
          const { campaign: cc, ...nmpe } = mpe
          campaigns[campaign.id].events.set.push(nmpe)
        } else if (advertisable) {
          if (!advertisables[advertisable.id]) {
            let na = { ...advertisable, events: { set: [] }}
            items.push(na)
            advertisables[advertisable.id] = na
          }
          advertisables[advertisable.id].events.set.push(mpe)
        } else {
          items.push(mpe)
        }
      }

      // Display singular mpe's as it's own item
      for (let i = 0; i < items.length; i++) {
        const mpe = items[i]
        if (mpe.__typename === 'Advertisable') {
          if (mpe.events.set.length <= 1) {
            items.splice(i, 1, mpe.events.set[0])
          }
        }
      }

      return { displayItems: [ ...items ], campaigns: [] }
    },

    /**
     * Returns raw MPE data directly from the query. If tour is running and user has no MPE's, than return some dummy data
     * @returns {Array} An array of MPE objects
     */
    getRawData () {
      let userMpes = this.mediaPlanEvents.set || []

      if (((this.$tours || {}).mainPageTour || {}).isRunning && !userMpes.length) return cvDummy.get()
      return userMpes
    },

    /**
     * Gives built grid items
     * @returns {Array} Array of built grid items
     */
    getData () {
      let rtr = this.prepareGridObjects.displayItems || []
      if (!this.isFiltering) {
        return (this.campaigns.set || []).concat(rtr)
          .sort(({ startsAt: saa, publishAt: paa, createdAt: caa }, { startsAt: sab, publishAt: pab, createdAt: cab }) => {
            return moment(sab || pab || cab).diff(moment(saa || paa || caa))
          })
      }
      return rtr
    },

    /**
     * Gives accepted mpe types
     * @returns {Array}
     */
    getAcceptedTypes () {
      return Array.from(acceptedTypes)
    },

    /**
     * Gives mpe query vars
     * @returns {Object}
     */
    getMpeQueryVars () {
      if (!this.isFiltering) {
        return {
          initialSet: true,
          limit: 12,
          ownedBy: localStorageHelper.getData('user', 'id'),
          standalone: true
        }
      }

      let dateRange = this.getDateRange || {}
      let extra = {
        limit: -1,
        ownedBy: localStorageHelper.getData('user', 'id'),
        type: this.getAcceptedTypes
      }

      return Object.assign({}, dateRange, extra)
    },

    getCampaignQueryVars () {
      return {
        limit: 6,
        ownedBy: localStorageHelper.getData('user', 'id'),
        mpeType: this.getAcceptedTypes
      }
    },

    /**
     * Gives data for currently edited campaign
     */
    getEditedCampaignData () {
      return this.getData.filter(c => c.id === this.campaignId)[0] || {}
    }
  },

  mixins: [
    apiConnector,
    duplicator
  ],

  // Apollo...
  apollo: {
    firstLast: {
      query: gql`query {
        first:campaigns(sort:[startsAt],limit:1){set{startsAt}}
        last:campaigns(sort:[expiresAt, DESC],limit:1){set{expiresAt}}
      }`,
      fetchPolicy: 'cache-and-network',
      update (data) {
        let { first, last } = data
        this.$set(this, 'campaignRange', {
          first: (first.set[0] || {}).startsAt,
          last: (last.set[0] || {}).expiresAt
        })
      }
    },

    campaigns: {
      query: gql`query ($ownedBy: [UUID!], $limit: Int!, $mpeType: [MediaPlanEventType!]) {
        campaigns (ownedBy: $ownedBy, sort: [startsAt, DESC], limit: $limit) {
          set {
            ${campaign()}
            events (type: $mpeType) {
              set {
                ${event()}
                payload {
                  ${eventPayload()}
                }
                advertisable {
                  ${advertisable()}
                }
              }
            }
          }
        }
      }`,

      variables () {
        return this.getCampaignQueryVars
      },

      fetchPolicy: 'cache-and-network'
    },

    mediaPlanEvents: {
      query: gql`query ($from: Time, $to: Time, $ownedBy: [UUID!], $type: [MediaPlanEventType!], $limit: Int!, $initialSet: Boolean!, $standalone: Boolean!) {
        mediaPlanEvents (scheduledFrom: $from,  scheduledUntil: $to, limit: $limit, ownedBy: $ownedBy, type: $type, initialSet: $initialSet, standalone: $standalone) {
          set {
            ${event()}

            advertisable {
              ${advertisable()}
            },

            campaign {
              ${campaign()}
            }

            payload {
              ${eventPayload()}
            }
          }
        }
      }`,

      variables () {
        return this.getMpeQueryVars
      },

      fetchPolicy: 'cache-and-network'
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/pagination/providers/campaignViewer.js